import { candidatesStore } from 'components/candidates/CandidatesStore';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import EmptyState from 'ats/components/empty-state/EmptyState';
import ActionMenu from 'ats/components/action-menu/ActionMenu';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import { modalStore } from 'components/layout/modals/ModalStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import InviteCandidateModal from './InviteCandidateModal';
import ApplicantPublicProfileContent, {
	hasPublicProfileContent
} from './ApplicantPublicProfileContent';
import MigratePublicProfileModal from './MigratePublicProfileModal';
import { applicantStore } from '../ApplicantStore';
import { hasPermission, PERMISSIONS } from 'shared/security/rolePermissions';
import './ApplicantPublicProfileStatus.sass';

export const PUBLIC_PROFILE_STATUS = {
	NONE: 'none',
	UNCLAIMED: 'unclaimed',
	CLAIMED: 'claimed'
};

export const INVITE_CANDIDATE_LABEL = 'Invite to Mira';

export function getPublicProfileStatus(applicant) {
	if (applicant?.publicProfileStatus) return applicant.publicProfileStatus;
	return applicant?.candidate?.id
		? PUBLIC_PROFILE_STATUS.CLAIMED
		: PUBLIC_PROFILE_STATUS.NONE;
}

export function openInviteCandidateModal(applicant, onSuccess) {
	modalStore.openModal(
		{ title: INVITE_CANDIDATE_LABEL, size: 'md', position: 'center' },
		<InviteCandidateModal applicant={applicant} handleSuccess={onSuccess} />
	);
}

export async function createPublicProfileForApplicant(applicant, onSuccess) {
	const canEditProfile = hasPermission(PERMISSIONS.CANDIDATE_PROFILE_EDIT);
	// Opened synchronously so popup blockers treat it as user-initiated.
	const editorWindow = canEditProfile ? window.open('', '_blank') : null;
	const result = await applicantStore.createPublicProfile(applicant.id);

	if (result?.status === 'success') {
		notificationStore.pushNotification({
			type: notificationStore.SUCCESS_NOTIFICATION,
			title: 'Public Profile',
			message: 'Public Profile created. You can now fill it in.'
		});
		onSuccess && onSuccess();
		if (editorWindow && result.data?.candidateId) {
			editorWindow.location.href = `/ats/candidates/profile/${result.data.candidateId}/edit`;
		} else if (editorWindow) {
			editorWindow.close();
		}
	} else {
		editorWindow && editorWindow.close();
		notificationStore.pushErrorNotification(
			'Public Profile',
			result?.message || 'Failed to create the Public Profile.'
		);
	}
	return result;
}

const formatDate = value =>
	value ? moment(value).format('MMM D, YYYY') : null;

function openProfileEditor(candidateId) {
	window.open(`/ats/candidates/profile/${candidateId}/edit`, '_blank');
}

function UnclaimedProfileBanner({
	applicant,
	canEdit,
	canEditProfile,
	onInvite
}) {
	const createdBy = applicant?.publicProfileCreatedBy?.name;
	const createdAt = formatDate(applicant?.publicProfileCreatedAt);
	const invitedAt = formatDate(applicant?.lastInvitationSentAt);
	const candidateId = applicant?.candidate?.id;

	return (
		<div className="public-profile-status-banner">
			<div className="public-profile-status-banner__text">
				<p className="public-profile-status-banner__title">
					Candidate hasn't registered in MIRA
				</p>
				<p className="public-profile-status-banner__detail">
					{createdAt
						? `Public Profile created${createdBy ? ` by ${createdBy}` : ''} on ${createdAt}. `
						: ''}
					{invitedAt
						? `Last invitation sent on ${invitedAt}.`
						: 'No invitation sent yet.'}
				</p>
			</div>
			<div className="public-profile-status-banner__actions">
				{canEditProfile && candidateId && (
					<button
						type="button"
						className="ats-button-soft"
						onClick={() => openProfileEditor(candidateId)}>
						Edit Public Profile
					</button>
				)}
				{canEdit && (
					<button type="button" className="ats-button-soft" onClick={onInvite}>
						{INVITE_CANDIDATE_LABEL}
					</button>
				)}
			</div>
		</div>
	);
}

export default function ApplicantPublicProfile({ applicant, onSuccess }) {
	const [candidate, setCandidate] = useState(null);
	const [loading, setLoading] = useState(Boolean(applicant?.candidate?.id));
	const [creating, setCreating] = useState(false);
	const candidateId = applicant?.candidate?.id;
	const status = getPublicProfileStatus(applicant);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			if (!candidateId) {
				setCandidate(null);
				setLoading(false);
				return;
			}

			setLoading(true);
			const result = await candidatesStore.get(candidateId);
			if (cancelled) return;

			if (result?.status === 'success' && result.data) {
				setCandidate(result.data);
			} else {
				setCandidate(null);
			}
			setLoading(false);
		})();

		return () => {
			cancelled = true;
		};
	}, [candidateId]);

	const canEdit = hasPermission(PERMISSIONS.APPLICANT_EDIT);
	const canEditProfile = hasPermission(PERMISSIONS.CANDIDATE_PROFILE_EDIT);

	const handleInviteCandidate = () =>
		openInviteCandidateModal(applicant, onSuccess);

	const handleCreatePublicProfile = async () => {
		if (creating) return;
		setCreating(true);
		await createPublicProfileForApplicant(applicant, onSuccess);
		setCreating(false);
	};

	const handleMigratePublicProfile = () => {
		modalStore.openModal(
			{
				title: 'Migrate a Public Profile',
				size: 'md',
				position: 'center',
				saveButtonLabel: 'Migrate'
			},
			<MigratePublicProfileModal
				applicant={applicant}
				handleSuccess={onSuccess}
			/>
		);
	};

	const renderEmptyStateActions = () => {
		if (!canEdit) return null;

		const profileMenuItems = [];
		if (status === PUBLIC_PROFILE_STATUS.NONE) {
			profileMenuItems.push({
				label: 'Create Public Profile',
				onClick: handleCreatePublicProfile
			});
		}
		if (!candidateId) {
			profileMenuItems.push({
				label: 'Migrate a Public Profile',
				onClick: handleMigratePublicProfile
			});
		}
		const showInvite = status !== PUBLIC_PROFILE_STATUS.CLAIMED;

		if (!profileMenuItems.length && !showInvite) return null;

		return (
			<div className="public-profile-empty-actions">
				{profileMenuItems.length > 0 && (
					<ActionMenu
						position="left"
						className="public-profile-menu"
						buttonClassName="public-profile-menu__button"
						menuClassName="public-profile-menu__items"
						buttonText="Public Profile"
						buttonIcon={
							<img
								src="/ats/icons/chevron-down-16-icon.svg"
								alt=""
								width={16}
								height={16}
								className="public-profile-menu__chevron"
							/>
						}
						items={profileMenuItems}
					/>
				)}
				{showInvite && (
					<button
						type="button"
						className="public-profile-invite-button"
						onClick={handleInviteCandidate}>
						{INVITE_CANDIDATE_LABEL}
					</button>
				)}
			</div>
		);
	};

	const renderEmptyState = () => (
		<div className="applicant-profile-tab-empty applicant-profile-tab-empty--profile">
			<EmptyState
				icon={EMPTY_STATE_ICONS.inviteTalentNetwork}
				iconAlt="No public profile"
				message="Profile details are not available yet."
				actionsInText
				actionsContent={renderEmptyStateActions()}
			/>
		</div>
	);

	if (loading) {
		return null;
	}

	const showProfileContent = candidate && hasPublicProfileContent(candidate);

	if (status === PUBLIC_PROFILE_STATUS.UNCLAIMED && candidateId) {
		return (
			<div className="applicant-public-profile-unclaimed">
				<UnclaimedProfileBanner
					applicant={applicant}
					canEdit={canEdit}
					canEditProfile={canEditProfile}
					onInvite={handleInviteCandidate}
				/>
				{showProfileContent ? (
					<div className="applicant-public-profile applicant-public-profile--redesign CandidateProfile public-profile">
						<ApplicantPublicProfileContent candidate={candidate} />
					</div>
				) : (
					<div className="applicant-profile-tab-empty applicant-profile-tab-empty--profile">
						<EmptyState
							icon={EMPTY_STATE_ICONS.inviteTalentNetwork}
							iconAlt="Empty public profile"
							message="Profile is empty, start filling it in."
							actionsInText
							actions={
								canEditProfile
									? [
											{
												text: 'Edit Public Profile',
												onClick: () => openProfileEditor(candidateId)
											}
										]
									: []
							}
						/>
					</div>
				)}
			</div>
		);
	}

	if (showProfileContent) {
		return (
			<div className="applicant-public-profile applicant-public-profile--redesign CandidateProfile public-profile">
				<ApplicantPublicProfileContent candidate={candidate} />
			</div>
		);
	}

	return (
		<div className="applicant-public-profile no-profile-found">
			{renderEmptyState()}
		</div>
	);
}
